import { Clock } from 'lucide-react'
import type { ServiceStatus } from '../api/serviceConfigApi'
import { formatDate } from '@/utils/timeZoneUtils'

interface ServiceActivityListProps {
  recentServices: Array<ServiceStatus>
}

export const ServiceActivityList = ({
  recentServices,
}: ServiceActivityListProps) => {
  const formatStatus = (status: string) => {
    const statusColors = {
      pending: 'text-yellow-600',
      running: 'text-blue-600',
      completed: 'text-green-600',
      failed: 'text-red-600',
      missed: 'text-orange-600',
    }
    return (
      <span
        className={`font-medium ${statusColors[status as keyof typeof statusColors] || 'text-gray-600'}`}
      >
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    )
  }

  const formatDuration = (duration?: number) => {
    if (!duration) return 'N/A'
    if (duration < 1000) return `${duration}ms`
    return `${(duration / 1000).toFixed(1)}s`
  }

  if (recentServices.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Clock className="mx-auto h-8 w-8 mb-2" />
        <p>No recent service activity</p>
        <p className="text-sm text-gray-400 mt-1">
          Service executions will appear here once scheduled tasks run
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
      {recentServices.map((service, index) => (
        <div
          key={`${service.serviceName}-${service.scheduledTime}-${index}`}
          className="border rounded-lg p-4"
        >
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium">
              {service.serviceName === 'email_fetch'
                ? 'Email Fetch Service'
                : 'Notification Send Service'}
            </h4>
            {formatStatus(service.status)}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
            <div>
              <span className="font-medium">Scheduled:</span>
              <br />
              {formatDate(service.scheduledTime, 'DD/MM/YYYY hh:mm:ss A')}
            </div>
            {service.actualRunTime && (
              <div>
                <span className="font-medium">Executed:</span>
                <br />
                {formatDate(service.actualRunTime, 'DD/MM/YYYY hh:mm:ss A')}
              </div>
            )}
            <div>
              <span className="font-medium">Duration:</span>
              <br />
              {formatDuration(service.duration)}
            </div>
            <div>
              <span className="font-medium">Processed:</span>
              <br />
              {service.processedCount || 0} items
            </div>
          </div>
          {service.errorMessage && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              <strong>Error:</strong> {service.errorMessage}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
